import { useEffect, useState } from "react";
import api, { formatApiError, money, fmtDate, fmtHours } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import RichTextView from "../components/RichTextView";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Clock, User } from "lucide-react";

const statusPt = (s) => ({ aguardando_aprovacao: "Aguardando aprovação" }[s] || s);

export default function Aprovacoes() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const [t, c, u] = await Promise.all([
        api.get("/tickets", { params: { status: "aguardando_aprovacao" } }),
        api.get("/clients"),
        api.get("/users"),
      ]);
      setItems(t.data); setClients(c.data); setUsers(u.data);
    } catch { /* Correções de homologação (P1) — nunca deixar a promise sem tratamento */ }
  };
  useEffect(() => { load(); }, []);

  const cliName = (id) => clients.find(c => c.id === id)?.razao_social || "-";
  const userName = (id) => users.find(u => u.id === id)?.name || "-";

  const approve = async (t) => {
    try { await api.post(`/tickets/${t.id}/approve`); toast.success("Ficha aprovada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doReject = async () => {
    if (!reason.trim()) return toast.error("Informe o motivo");
    try {
      await api.post(`/tickets/${rejectOpen.id}/reject-approval`, { reason });
      toast.success("Ficha devolvida ao analista"); setRejectOpen(null); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Aprovações</h1>
          <p className="text-slate-500 text-sm">Fichas encerradas pelos analistas aguardando sua aprovação</p>
        </div>
        <Badge className="bg-purple-100 text-purple-800 text-sm px-3 py-1" data-testid="aprovacoes-count">{items.length} pendentes</Badge>
      </div>

      {items.length === 0 && (
        <Card className="p-12 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <ThumbsUp className="text-emerald-600" size={24} />
          </div>
          <div className="mt-3 font-heading font-semibold text-slate-900">Tudo em dia</div>
          <div className="text-sm text-slate-500 mt-1">Nenhuma ficha aguardando aprovação no momento.</div>
        </Card>
      )}

      <div className="space-y-3">
        {items.map(t => (
          <Card key={t.id} className="p-5" data-testid={`aprovacao-${t.numero}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-lg text-slate-900">Ficha #{t.numero}</span>
                  <Badge className="bg-purple-100 text-purple-800">{statusPt(t.status)}</Badge>
                  <Badge className="bg-slate-100 text-slate-700">{t.tipo_servico}</Badge>
                </div>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-slate-500">Cliente</div><div className="font-medium">{cliName(t.cliente_id)}</div></div>
                  <div><div className="text-xs text-slate-500">Analista</div><div className="font-medium flex items-center gap-1"><User size={12} />{userName(t.analista_id)}</div></div>
                  <div><div className="text-xs text-slate-500">Data</div><div className="font-medium">{fmtDate(t.data_servico)}</div></div>
                  <div><div className="text-xs text-slate-500">Horas</div><div className="font-medium flex items-center gap-1"><Clock size={12} />{fmtHours(t.horas_realizadas)}</div></div>
                </div>
                {t.descricao && (
                  <div className="mt-3">
                    <div className="text-xs text-slate-500">Descrição / Histórico do Serviço</div>
                    <RichTextView html={t.descricao} className="text-sm text-slate-700 mt-1" />
                  </div>
                )}
                {t.pendencias && <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">Pendências: {t.pendencias}</div>}
                <div className="mt-3 text-lg font-heading font-bold text-primary">{money(t.valor_total)}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => { setRejectOpen(t); setReason(""); }} data-testid={`btn-reject-${t.numero}`}>
                  <ThumbsDown size={16} /> Rejeitar
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(t)} data-testid={`btn-approve-${t.numero}`}>
                  <ThumbsUp size={16} /> Aprovar
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!rejectOpen} onOpenChange={v => !v && setRejectOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ThumbsDown className="text-red-600" size={18} /> Rejeitar Ficha #{rejectOpen?.numero}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">A ficha voltará para o analista com o motivo abaixo.</p>
            <div><Label>Motivo</Label><Textarea rows={4} value={reason} onChange={e => setReason(e.target.value)} data-testid="reject-reason-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doReject} data-testid="confirm-reject">Rejeitar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
